import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthInitialised, useCan, useCurrentUser } from '@/app/hooks';
import { Spinner } from '@/components/Spinner';
import type { Capability } from './types';

interface Props {
  children: ReactNode;
  /** Every capability listed must be held. */
  capabilities?: Capability[];
}

/**
 * Route guard. This keeps people out of screens that would only error, and is
 * not a security boundary — the API enforces the same rules independently.
 */
export function RequireAuth({ children, capabilities = [] }: Props) {
  const user = useCurrentUser();
  const initialised = useAuthInitialised();
  const can = useCan();
  const location = useLocation();

  if (!initialised) return <Spinner label="Checking your session" />;

  if (!user) {
    return <Navigate to="/sign-in" state={{ from: location.pathname }} replace />;
  }

  // A forced password change blocks everything except the change screen.
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  const missing = capabilities.filter((c) => !can(c));
  if (missing.length > 0) {
    return (
      <div className="panel panel--notice">
        <h2>Not available to your account</h2>
        <p>You do not have access to this section. Speak to HR if you think that is wrong.</p>
      </div>
    );
  }

  return <>{children}</>;
}
